import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { ArchiveRestore, Loader2, ArrowLeft } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from "@/components/ui/pagination";
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/contexts/AuthContext";
import { API_CONFIG } from "@/config/envConfig";
import { graphqlClient } from "@/services/core/graphqlClient";
import { GROUPS_QUERIES } from "@/services/resources/groupsGquery";

interface ArchivedPatientGroup {
  patient_group_id: number;
  group_name: string;
  patient_count: number;
  is_archived: boolean;
  group_filters?: any;
}

const ArchivedPatientGroups = () => {
  const [archivedGroups, setArchivedGroups] = useState<ArchivedPatientGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesPerPage] = useState(10);
  const [isUnarchiveDialogOpen, setIsUnarchiveDialogOpen] = useState(false);
  const [groupToUnarchive, setGroupToUnarchive] = useState<string>("");
  const [isUnarchiving, setIsUnarchiving] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  const { token, hasuraToken, user } = useAuth();

  useEffect(() => {
    document.title = "Archived Patient Groups | Admin";
    fetchArchivedGroups();
  }, []);

  const fetchArchivedGroups = async () => {
    try {
      setLoading(true);
      const query = `
        query MyQuery {
          pep_vw_patient_group_dashboard(where: {is_archived: {_eq: true}}, order_by: {patient_group_id: desc}) {
            group_filters
            group_name
            is_archived
            patient_count
            patient_group_id
          }
        }
      `;

      const response = await graphqlClient.query(query, {}, hasuraToken);

      if (response.success && response.data?.pep_vw_patient_group_dashboard) {
        setArchivedGroups(response.data.pep_vw_patient_group_dashboard);
      } else {
        setArchivedGroups([]);
      }
    } catch (error) {
      console.error("Error fetching archived groups:", error);
      toast({
        title: "Error",
        description: "Failed to fetch archived groups",
        variant: "destructive",
      });
      setArchivedGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const handleUnarchiveClick = (groupId: string) => {
    setGroupToUnarchive(groupId);
    setIsUnarchiveDialogOpen(true);
  };

  const handleUnarchiveConfirm = async () => {
    if (!groupToUnarchive || !user?.id) return;

    try {
      setIsUnarchiving(true);

      const response = await fetch(
        `${API_CONFIG.API_BASE}/api/ngpep/${user.id}/patient-group/${groupToUnarchive}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            isArchived: false,
            patientIdsToAdd: [],
            patientIdsToRemove: [],
          }),
        }
      );

      if (response.ok) {
        toast({
          title: "Success",
          description: "Patient group unarchived successfully",
        });
        
        // Navigate back to patient groups list
        navigate("/admin/patient-groups");
      } else {
        const errorData = await response.json();
        toast({
          title: "Error",
          description: errorData.message || "Failed to unarchive patient group",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error unarchiving group:", error);
      toast({
        title: "Error",
        description: "Failed to unarchive patient group",
        variant: "destructive",
      });
    } finally {
      setIsUnarchiving(false);
      setIsUnarchiveDialogOpen(false);
      setGroupToUnarchive("");
    }
  };

  // Pagination logic
  const indexOfLastEntry = currentPage * entriesPerPage;
  const indexOfFirstEntry = indexOfLastEntry - entriesPerPage;
  const currentGroups = archivedGroups.slice(indexOfFirstEntry, indexOfLastEntry);
  const totalPages = Math.ceil(archivedGroups.length / entriesPerPage);

  const getPageNumbers = () => {
    const pageNumbers = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pageNumbers.push(i);
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pageNumbers.push(i);
      } else {
        pageNumbers.push(1);
        pageNumbers.push('...');
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pageNumbers.push(i);
        pageNumbers.push('...');
        pageNumbers.push(totalPages);
      }
    }
    
    return pageNumbers;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className="container mx-auto py-6 px-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate("/admin/patient-groups")}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Patient Groups
          </Button>
          <h1 className="text-3xl font-bold">Archived Patient Groups</h1>
        </div>
      </div>

      {/* Table */}
      <div id="table-admin-archived-patient-groups-container" className="bg-card rounded-lg border shadow-sm">
        <div className="p-6">
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : archivedGroups.length === 0 ? (
            <div id="row-archived-groups-empty" className="text-center py-12">
              <p className="text-muted-foreground">No archived groups found</p>
            </div>
          ) : (
            <>
              <Table id="table-admin-archived-patient-groups">
                <TableHeader>
                  <TableRow id="table-admin-archived-patient-groups-header-row">
                    <TableHead id="header-cell-group-id" className="w-[100px]">Group ID</TableHead>
                    <TableHead id="header-cell-group-name">Group Name</TableHead>
                    <TableHead id="header-cell-patient-count" className="text-center">Patient Count</TableHead>
                    <TableHead id="header-cell-status" className="text-center">Status</TableHead>
                    <TableHead id="header-cell-actions" className="text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody id="table-admin-archived-patient-groups-body">
                  {currentGroups.map((group) => (
                    <TableRow key={group.patient_group_id} id={`row-archived-patient-group-${group.patient_group_id}`}>
                      <TableCell id={`cell-archived-patient-group-${group.patient_group_id}-id`} className="font-medium">
                        {group.patient_group_id}
                      </TableCell>
                      <TableCell id={`cell-archived-patient-group-${group.patient_group_id}-name`}>{group.group_name}</TableCell>
                      <TableCell id={`cell-archived-patient-group-${group.patient_group_id}-count`} className="text-center">
                        {group.patient_count || 0}
                      </TableCell>
                      <TableCell id={`cell-archived-patient-group-${group.patient_group_id}-status`} className="text-center">
                        <Badge variant="secondary">Archived</Badge>
                      </TableCell>
                      <TableCell id={`cell-archived-patient-group-${group.patient_group_id}-actions`} className="text-center">
                        <Button
                          id={`btn-archived-patient-group-${group.patient_group_id}-unarchive`}
                          variant="outline"
                          size="sm"
                          onClick={() => handleUnarchiveClick(group.patient_group_id.toString())}
                        >
                          <ArchiveRestore className="h-4 w-4 mr-2" />
                          Unarchive
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="mt-4 flex justify-center">
                  <Pagination>
                    <PaginationContent>
                      <PaginationItem>
                        <PaginationPrevious
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          className={currentPage === 1 ? "pointer-events-none opacity-50" : "cursor-pointer"}
                        />
                      </PaginationItem>
                      
                      {getPageNumbers().map((pageNum, idx) => (
                        <PaginationItem key={idx}>
                          {pageNum === '...' ? (
                            <span className="px-4">...</span>
                          ) : (
                            <PaginationLink
                              onClick={() => setCurrentPage(pageNum as number)}
                              isActive={currentPage === pageNum}
                              className="cursor-pointer"
                            >
                              {pageNum}
                            </PaginationLink>
                          )}
                        </PaginationItem>
                      ))}

                      <PaginationItem>
                        <PaginationNext
                          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                          className={currentPage === totalPages ? "pointer-events-none opacity-50" : "cursor-pointer"}
                        />
                      </PaginationItem>
                    </PaginationContent>
                  </Pagination>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Unarchive Confirmation Dialog */}
      <AlertDialog open={isUnarchiveDialogOpen} onOpenChange={setIsUnarchiveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to unarchive this patient group? This will restore the group to the active patient groups list.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isUnarchiving}>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleUnarchiveConfirm}
              disabled={isUnarchiving}
              className="bg-primary"
            >
              {isUnarchiving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Unarchiving...
                </>
              ) : (
                "Unarchive"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ArchivedPatientGroups;
